import React, { useState, useEffect, useMemo } from 'react';
import adminApi, { adminSeasonsAPI } from '../services/adminApi';
import { getPromotionRelegationTransitionStyle } from '../utils/promotionRelegationEmojis';
import {
  buildDivisionActionMap,
  formatSuggestionLabel,
  isForfeitTeam,
  suggestNextDivisionId,
} from '../utils/promotionRelegationSuggestions';

/**
 * Mid-Season (completed) → Final Sprint (next): ⏫️ / ⏬️ in the suggestion column.
 * Opening → Mid-Season and all other pairs: ⬆️ / ⬇️ (unchanged).
 */

const AdminPromotionRelegation = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Season selection
  const [completedSeasonId, setCompletedSeasonId] = useState('');
  const [nextSeasonId, setNextSeasonId] = useState('');
  const [seasons, setSeasons] = useState([]);

  // Data
  const [standings, setStandings] = useState([]);
  const [nextSeasonDivisions, setNextSeasonDivisions] = useState({});
  const [placements, setPlacements] = useState({}); // { teamId: divisionId }
  /** Placements already saved via promotion_relegation_log (green “Placed” badge). */
  const [logPlacements, setLogPlacements] = useState({});
  /** Admin manually changed these rows — autofill must not overwrite. */
  const [userTouched, setUserTouched] = useState({});
  /** Authoritative completed-season row from GET promotion-relegation (names match DB, incl. dashes). */
  const [completedSeasonFromApi, setCompletedSeasonFromApi] = useState(null);

  useEffect(() => {
    setCompletedSeasonFromApi(null);
  }, [completedSeasonId]);

  // Fetch all seasons
  useEffect(() => {
    fetchSeasons();
  }, []);

  const fetchSeasons = async () => {
    try {
      const response = await adminSeasonsAPI.getAll();
      if (response.success && response.data) {
        setSeasons(response.data || []);
      } else {
        setSeasons([]);
      }
    } catch (err) {
      console.error('Error fetching seasons:', err);
      setError('Failed to load phases');
      setSeasons([]);
    }
  };

  // Fetch completed season standings
  const fetchStandings = async () => {
    if (!completedSeasonId) return;

    setLoading(true);
    setError('');
    try {
      // Include nextSeasonId in query if available to get existing placements
      const url = nextSeasonId 
        ? `/admin/promotion-relegation/${completedSeasonId}?nextSeasonId=${nextSeasonId}`
        : `/admin/promotion-relegation/${completedSeasonId}`;
      
      const response = await adminApi.get(url);
      
      // adminApi interceptor returns response.data, so response is already unwrapped
      // Backend returns: { success: true, data: { season: {...}, divisions: [...], existing_placements: {...} } }
      const divisions = response?.data?.divisions || [];
      const existingPlacements = response?.data?.existing_placements || {};
      const seasonFromResponse = response?.data?.season || null;
      setCompletedSeasonFromApi(seasonFromResponse);

      setStandings(divisions);
      
      // Pre-populate from promotion_relegation_log only (authoritative saved placements)
      const simplifiedLog = {};
      Object.keys(existingPlacements).forEach(teamId => {
        simplifiedLog[teamId] = existingPlacements[teamId].division_id;
      });
      setLogPlacements(simplifiedLog);

      if (Object.keys(simplifiedLog).length > 0) {
        console.log('✅ Pre-populating placements from promotion system:', simplifiedLog);
        // Log placements win over autofill suggestions
        setPlacements((prev) => ({ ...prev, ...simplifiedLog }));
      } else {
        console.log('ℹ️ No existing promotion placements found - will autofill from rules when next divisions load');
      }
      
      if (divisions.length === 0) {
        console.warn('⚠️ No divisions found in response. Response structure:', response);
      }
    } catch (err) {
      console.error('Error fetching standings:', err);
      setError('Failed to load standings');
    } finally {
      setLoading(false);
    }
  };

  // Fetch next season divisions
  const fetchNextSeasonDivisions = async () => {
    if (!completedSeasonId || !nextSeasonId) {
      console.log('⚠️ Cannot fetch next season divisions - missing IDs:', { completedSeasonId, nextSeasonId });
      return;
    }

    console.log('🔍 Fetching next season divisions...', { completedSeasonId, nextSeasonId });
    setLoading(true);
    setError('');
    try {
      const response = await adminApi.get(
        `/admin/promotion-relegation/${completedSeasonId}/next-season-divisions?nextSeasonId=${nextSeasonId}`
      );
      
      // adminApi interceptor returns response.data, so response is already unwrapped
      const divisionsByCanton = response?.data?.divisions_by_canton || {};
      
      setNextSeasonDivisions(divisionsByCanton);
      
      if (Object.keys(divisionsByCanton).length === 0) {
        console.warn('⚠️ No divisions found. Response:', response);
        setError('No divisions found. Please ensure divisions exist in the system.');
      }
    } catch (err) {
      console.error('❌ Error fetching next season divisions:', err);
      setError('Failed to load next phase divisions');
    } finally {
      setLoading(false);
    }
  };

  // When seasons are selected, fetch data
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (completedSeasonId) {
      fetchStandings();
    }
  }, [completedSeasonId, nextSeasonId]);  // Also refetch when nextSeasonId changes
  // Note: fetchStandings intentionally excluded - it's recreated on each render and would cause infinite loops

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (completedSeasonId && nextSeasonId) {
      // Clear placement state when seasons change to prevent stale data
      setPlacements({});
      setLogPlacements({});
      setUserTouched({});
      fetchNextSeasonDivisions();
    }
  }, [completedSeasonId, nextSeasonId]);
  // Note: fetchNextSeasonDivisions intentionally excluded - it's recreated on each render and would cause infinite loops

  const promotionEmojiStyle = useMemo(() => {
    const completed =
      completedSeasonFromApi ||
      seasons.find((s) => String(s.id) === String(completedSeasonId));
    const next = seasons.find((s) => String(s.id) === String(nextSeasonId));
    return getPromotionRelegationTransitionStyle(completed, next);
  }, [seasons, completedSeasonId, nextSeasonId, completedSeasonFromApi]);

  /** Per-division action map (promote / relegate / stay), forfeit-aware */
  const divisionActionMaps = useMemo(() => {
    const maps = {};
    standings.forEach((division) => {
      maps[division.id] = buildDivisionActionMap(division.standings, division.team_count);
    });
    return maps;
  }, [standings]);

  // Autofill empty selectors from rules (log placements and user edits win)
  useEffect(() => {
    if (!standings.length || !Object.keys(nextSeasonDivisions).length) return;

    setPlacements((prev) => {
      const next = { ...prev };
      let changed = false;

      standings.forEach((division) => {
        const actionMap =
          divisionActionMaps[division.id] ||
          buildDivisionActionMap(division.standings, division.team_count);
        const available = nextSeasonDivisions[division.canton] || [];

        division.standings.forEach((team) => {
          const tid = team.team_id;
          if (logPlacements[tid]) {
            if (next[tid] !== logPlacements[tid]) {
              next[tid] = logPlacements[tid];
              changed = true;
            }
            return;
          }
          if (userTouched[tid]) return;
          if (next[tid]) return;

          const action = actionMap.get(String(tid)) || 'manual';
          const suggestedId = suggestNextDivisionId({
            action,
            currentSkillMin: division.skill_level_min,
            nextDivisions: available,
          });
          if (suggestedId) {
            next[tid] = suggestedId;
            changed = true;
          }
        });
      });

      return changed ? next : prev;
    });
  }, [standings, nextSeasonDivisions, logPlacements, userTouched, divisionActionMaps]);

  // Handle placement change
  const handlePlacementChange = (teamId, divisionId, currentPlacement) => {
    // If team already has a placement, show confirmation
    if (currentPlacement && currentPlacement !== divisionId && divisionId !== '') {
      const currentDivisionName = getExistingPlacementInfo(teamId) || 'another division';
      const newDivisionName = nextSeasonDivisions[Object.keys(nextSeasonDivisions)[0]]
        ?.find(d => d.id === divisionId)?.name || 'selected division';
      
      const confirmed = window.confirm(
        `Team already placed in "${currentDivisionName}".\n\n` +
        `Change to "${newDivisionName}"?\n\n` +
        `This will overwrite the existing placement.`
      );
      
      if (!confirmed) {
        return; // Don't change if user cancels
      }
    }

    setUserTouched((prev) => ({ ...prev, [teamId]: true }));
    setPlacements(prev => ({
      ...prev,
      [teamId]: divisionId
    }));
  };

  // Get suggested action (promote/relegate/stay) — forfeit-aware, includes 12/14
  const getSuggestedAction = (team, division) => {
    const actionMap = divisionActionMaps[division.id];
    const action = actionMap?.get(String(team.team_id)) || 'manual';
    return formatSuggestionLabel({
      action,
      emojiStyle: promotionEmojiStyle,
      isForfeit: isForfeitTeam(team),
    });
  };

  // Apply placements
  const handleApplyPlacements = async () => {
    if (!completedSeasonId || !nextSeasonId) {
      setError('Please select both completed phase and next phase');
      return;
    }

    // Build placements array
    const placementsArray = [];
    standings.forEach(division => {
      division.standings.forEach(team => {
        const nextDivisionId = placements[team.team_id];
        if (nextDivisionId) {
          const suggestion = getSuggestedAction(team, division);
          placementsArray.push({
            team_id: team.team_id,
            next_season_division_id: nextDivisionId,
            action: suggestion.action
          });
        }
      });
    });

    if (placementsArray.length === 0) {
      setError('No placements to apply. Please select divisions for teams.');
      return;
    }

    // Confirm before applying
    const confirmed = window.confirm(
      `Apply placements for ${placementsArray.length} teams to next phase?\n\n` +
      `This will update team divisions for the next phase based on their completed phase performance.`
    );

    if (!confirmed) return;

    setLoading(true);
    setError('');
    setSuccess('');

    // Reverse proxies (nginx, Cloudflare, many hosts) often cut long requests after ~30–60s.
    // The apply-placements route runs many DB round-trips *per team*, so a single big POST
    // frequently dies as "Request aborted" (no HTTP status) even with a 10m axios timeout.
    // Chunking keeps each request within typical proxy limits.
    const APPLY_CHUNK_SIZE = 5;

    let completedChunks = 0;
    try {
      const total = placementsArray.length;
      const chunks = [];
      for (let i = 0; i < total; i += APPLY_CHUNK_SIZE) {
        chunks.push(placementsArray.slice(i, i + APPLY_CHUNK_SIZE));
      }

      let teamsPlaced = 0;
      let promotions = 0;
      let relegations = 0;
      let stays = 0;

      for (let c = 0; c < chunks.length; c++) {
        if (chunks.length > 1) {
          setSuccess(
            `Applying placements… (batch ${c + 1} of ${chunks.length}, up to ${APPLY_CHUNK_SIZE} teams per batch)`
          );
        } else {
          setSuccess('Applying placements…');
        }
        // Per-batch timeout: each batch should stay under host/proxy limits
        const response = await adminApi.post(
          '/admin/promotion-relegation/apply-placements',
          {
            completed_season_id: completedSeasonId,
            next_season_id: nextSeasonId,
            placements: chunks[c],
          },
          { timeout: 300000 }
        );
        if (response && response.success === false) {
          throw new Error(response.message || 'Apply placements failed');
        }
        const d = response?.data;
        if (d) {
          teamsPlaced += d.teams_placed || 0;
          promotions += d.promotions || 0;
          relegations += d.relegations || 0;
          stays += d.stays || 0;
        }
        completedChunks++;
      }

      // Interceptor returns axios response.data: { success, data: { teams_placed, ... } }
      setSuccess(
        `Successfully placed ${teamsPlaced} teams. ` +
        `${promotions} promoted, ` +
        `${relegations} relegated, ` +
        `${stays} staying.`
      );

      if (completedSeasonId) {
        fetchStandings();
      }
    } catch (err) {
      console.error('Error applying placements:', err);
      const aborted =
        err.code === 'ECONNABORTED' ||
        (typeof err.message === 'string' && err.message.toLowerCase().includes('aborted')) ||
        (typeof err.message === 'string' && err.message.toLowerCase().includes('timeout'));
      let errorMessage = aborted
        ? 'The connection was cut before the server finished (often a 30–60s limit on the hosting reverse proxy, not this form). Batches of a few teams are sent now; if this still fails, try a smaller phase or check server logs.'
        : err.response?.data?.message || err.message || 'Failed to apply placements';

      if (completedChunks > 0) {
        errorMessage =
          `The first ${completedChunks} batch(es) may have been saved before this error. ` +
          `Refresh the page, confirm which teams are already placed, then only apply the rest if needed. ` +
          `Details: ${errorMessage}`;
      }

      if (err.response?.data?.error_type === 'wrong_season_divisions') {
        setError(
          errorMessage + '\n\n' +
          'Solution: Please refresh the page (F5) to reload divisions for the next phase, then try again.'
        );
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  // Get available divisions for a team (same canton)
  const getAvailableDivisions = (teamCanton) => {
    const cantonDivisions = nextSeasonDivisions[teamCanton] || [];
    // Sort by skill level descending (Elite first, Beginner last)
    return cantonDivisions.sort((a, b) => b.skill_level_min - a.skill_level_min);
  };

  // Get current placement for a team (only if placed via promotion system)
  const getCurrentPlacement = (teamId) => {
    // Only return placement if team is in placements state (which only contains promotion placements)
    return placements[teamId] || null;
  };

  // Check if a team has a promotion placement saved in the log (not just autofill)
  const hasPromotionPlacement = (teamId) => {
    const placement = logPlacements[teamId];
    return Boolean(placement && placement !== '');
  };

  // Get existing placement info for display
  const getExistingPlacementInfo = (teamId) => {
    const placement = placements[teamId];
    if (!placement) return null;
    
    // Find the division name from nextSeasonDivisions
    const divisionId = typeof placement === 'string' ? placement : placement.division_id || placement;
    
    for (const canton in nextSeasonDivisions) {
      const division = nextSeasonDivisions[canton].find(d => d.id === divisionId);
      if (division) {
        return division.name;
      }
    }
    return null;
  };

  // Count placements by action
  // IMPORTANT: Only count teams that are in the placements state (teams that were placed via promotion system)
  // This excludes teams that were auto-registered or moved via division balancing
  const getPlacementSummary = () => {
    let promotions = 0;
    let relegations = 0;
    let stays = 0;
    let totalPlaced = 0;

    standings.forEach(division => {
      division.standings.forEach(team => {
        const nextDivisionId = placements[team.team_id];
        // Only count if team has a placement (not empty string or null)
        if (nextDivisionId && nextDivisionId !== '') {
          totalPlaced++;
          const suggestion = getSuggestedAction(team, division);
          if (suggestion.action === 'promote') promotions++;
          else if (suggestion.action === 'relegate') relegations++;
          else stays++;
        }
      });
    });

    return { promotions, relegations, stays, total: totalPlaced };
  };

  const summary = getPlacementSummary();

  return (
    <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
      <h1>Team Placement for Next Phase</h1>
      <p style={{ color: '#666', marginBottom: '20px' }}>
        Place teams in next phase divisions based on their completed phase final ranking (reward/punishment).
      </p>

      {/* Season Selection */}
      <div style={{ marginBottom: '30px', display: 'flex', gap: '20px', alignItems: 'flex-end' }}>
        <div style={{ flex: 1 }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
            Completed Phase:
          </label>
          <select
            value={completedSeasonId}
            onChange={(e) => setCompletedSeasonId(e.target.value)}
            style={{ width: '100%', padding: '8px', fontSize: '14px' }}
          >
            <option value="">Select completed phase...</option>
            {seasons
              .filter(s => s.status === 'completed' || new Date(s.end_date) < new Date())
              .map(season => (
                <option key={season.id} value={season.id}>
                  {season.name} ({new Date(season.start_date).getFullYear()})
                </option>
              ))}
          </select>
        </div>

        <div style={{ flex: 1 }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
            Next Phase:
          </label>
          <select
            value={nextSeasonId}
            onChange={(e) => setNextSeasonId(e.target.value)}
            style={{ width: '100%', padding: '8px', fontSize: '14px' }}
          >
            <option value="">Select next phase...</option>
            {seasons
              .filter(s => s.status !== 'completed' && s.id !== completedSeasonId)
              .map(season => (
                <option key={season.id} value={season.id}>
                  {season.name} ({new Date(season.start_date).getFullYear()})
                </option>
              ))}
          </select>
        </div>
      </div>

      {completedSeasonId && nextSeasonId && promotionEmojiStyle === 'mid_to_final' && (
        <p style={{ marginBottom: '20px', padding: '10px 14px', backgroundColor: '#e3f2fd', borderRadius: '6px', fontSize: '14px' }}>
          <strong>Phase 2 up &amp; down:</strong> ⏫️ promote / ⏬️ relegate (Mid-Season → Final Sprint). Opening → Mid-Season still uses ⬆️ / ⬇️.
        </p>
      )}

      {/* Error/Success Messages */}
      {error && (
        <div style={{ padding: '10px', backgroundColor: '#fee', color: '#c00', marginBottom: '20px', borderRadius: '4px' }}>
          {error}
        </div>
      )}


      {success && (
        <div style={{ padding: '10px', backgroundColor: '#efe', color: '#0a0', marginBottom: '20px', borderRadius: '4px' }}>
          {success}
        </div>
      )}

      {/* Summary */}
      {summary.total > 0 && (
        <div style={{ padding: '15px', backgroundColor: '#f5f5f5', marginBottom: '20px', borderRadius: '4px' }}>
          <strong>Placement Summary:</strong> {summary.total} teams placed - 
          {summary.promotions > 0 && <span style={{ color: 'green' }}> {summary.promotions} promoting</span>}
          {summary.relegations > 0 && <span style={{ color: 'red' }}> {summary.relegations} relegating</span>}
          {summary.stays > 0 && <span> {summary.stays} staying</span>}
        </div>
      )}

      {/* Standings and Placement Interface */}
      {loading && <div style={{ textAlign: 'center', padding: '20px' }}>Loading...</div>}

      {!loading && standings.length > 0 && (
        <div>
          <h2>Completed Phase Final Standings → Next Phase Placement</h2>
          <p style={{ color: '#666', fontSize: '14px', marginTop: 0 }}>
            Selectors are pre-filled from promotion/relegation rules (including 12/14 and forfeit slots).
            Review and change any row, then Apply when ready — nothing is saved until you apply.
          </p>

          {standings.map(division => (
            <div key={division.id} style={{ marginBottom: '30px', border: '1px solid #ddd', borderRadius: '4px', padding: '15px' }}>
              <h3 style={{ marginTop: 0 }}>
                {division.name} ({division.canton}) - {division.team_count} teams
              </h3>

              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f5f5f5' }}>
                    <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Position</th>
                    <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Team</th>
                    <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Points</th>
                    <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>W-L</th>
                    <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Suggestion</th>
                    <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Place in Next Phase</th>
                  </tr>
                </thead>
                <tbody>
                  {division.standings.map((team) => {
                    const suggestion = getSuggestedAction(team, division);
                    const availableDivisions = getAvailableDivisions(division.canton);
                    const currentPlacement = getCurrentPlacement(team.team_id);
                    // Only show badge if team was placed via promotion system (not autofill)
                    const isPlacedViaPromotion = hasPromotionPlacement(team.team_id);

                    return (
                      <tr
                        key={team.team_id}
                        style={{
                          backgroundColor:
                            suggestion.action === 'promote' ? '#e8f5e9' :
                            suggestion.action === 'relegate' ? '#ffebee' :
                            'white'
                        }}
                      >
                        <td style={{ padding: '10px', borderBottom: '1px solid #eee' }}>
                          <strong>{team.position}</strong>
                        </td>
                        <td style={{ padding: '10px', borderBottom: '1px solid #eee' }}>
                          {team.team_name}
                        </td>
                        <td style={{ padding: '10px', borderBottom: '1px solid #eee' }}>
                          {team.points}
                        </td>
                        <td style={{ padding: '10px', borderBottom: '1px solid #eee' }}>
                          {team.wins}-{team.losses}
                        </td>
                        <td style={{ padding: '10px', borderBottom: '1px solid #eee' }}>
                          {suggestion.label}
                        </td>
                        <td style={{ padding: '10px', borderBottom: '1px solid #eee' }}>
                          {availableDivisions.length > 0 ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <select
                                value={currentPlacement || ''}
                                onChange={(e) => handlePlacementChange(team.team_id, e.target.value, currentPlacement)}
                                style={{ 
                                  flex: 1,
                                  padding: '5px',
                                  backgroundColor: isPlacedViaPromotion ? '#e8f5e9' : 'white',
                                  border: isPlacedViaPromotion ? '2px solid #4CAF50' : '1px solid #ddd'
                                }}
                              >
                                <option value="">Select division...</option>
                                {availableDivisions.map(div => {
                                  // Only show "✓ (Placed via Promotion)" if team was placed via promotion system AND this division matches
                                  const isPromotionPlacement = isPlacedViaPromotion && div.id === currentPlacement;
                                  return (
                                    <option key={div.id} value={div.id}>
                                      {div.name} {div.team_count > 0 ? `(${div.team_count} teams)` : ''}
                                      {isPromotionPlacement ? ' ✓ (Placed via Promotion)' : ''}
                                    </option>
                                  );
                                })}
                              </select>
                              {isPlacedViaPromotion && (
                                <span 
                                  style={{ 
                                    color: '#4CAF50', 
                                    fontSize: '12px',
                                    fontWeight: 'bold',
                                    whiteSpace: 'nowrap'
                                  }}
                                  title={`Placed via promotion system in ${getExistingPlacementInfo(team.team_id) || 'a division'}`}
                                >
                                  ✓ Placed
                                </span>
                              )}
                            </div>
                          ) : (
                            <span style={{ color: '#999', fontStyle: 'italic' }}>
                              No divisions available for {division.canton}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ))}

          {/* Apply Button */}
          <div style={{ marginTop: '30px', textAlign: 'center' }}>
            <button
              onClick={handleApplyPlacements}
              disabled={loading || summary.total === 0}
              style={{
                padding: '12px 24px',
                fontSize: '16px',
                backgroundColor: summary.total > 0 ? '#4CAF50' : '#ccc',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: summary.total > 0 ? 'pointer' : 'not-allowed',
                fontWeight: 'bold'
              }}
            >
              {loading ? 'Applying...' : 'Apply Placements to Next Phase'}
            </button>
          </div>

          <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#fff3cd', borderRadius: '4px', fontSize: '14px' }}>
            ⚠️ <strong>Note:</strong> Teams will START next phase in selected divisions, reflecting their completed phase performance. 
            Match generation will use these divisions.
          </div>
        </div>
      )}

      {!loading && standings.length === 0 && completedSeasonId && (
        <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
          No standings found for this phase. Make sure the phase has completed matches and final standings.
        </div>
      )}
    </div>
  );
};

export default AdminPromotionRelegation;

